import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, Group, Text, Tabs, Select } from '@mantine/core';
import { IconSettings, IconTruckDelivery, IconTool, IconCalendarStats, IconUsers } from '@tabler/icons-react';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import { CotizadorModelo } from '../../../modelos/CotizadorNBS';
import type { IPaisNBS } from '../../../modelos/CotizadorNBS/entidades/IPaisNBS';
import { useAvisoErrorConsulta } from './useAvisoErrorConsulta';
import { PaisConfiguracionContext } from './paisConfiguracion';
import { VariablesGlobalesCV } from './tabs/variablesGlobalesCV';
import { LogisticaCV } from './tabs/logisticaCV';
import { MoiAscensoresCV } from './tabs/moiAscensoresCV';
import { MoiEscalerasCV } from './tabs/moiEscalerasCV';
import { VariacionMensualCV } from './tabs/variacionMensualCV';
import { ComisionesCV } from './tabs/comisionesCV';

export const ConfiguracionCotizadorCV: React.FC = () => {
  const canceladoresRef = useRef<Record<string, () => void>>({});
  const { usuario } = useAutenticacion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  const [paises, setPaises] = useState<IPaisNBS[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>('globales');
  // País que se configura: arranca en el del usuario; el administrador comercial puede cambiar a los otros.
  const [paisId, setPaisId] = useState<number | undefined>(usuario?.PaisId);

  useEffect(() => {
    setPaisId(actual => actual ?? usuario?.PaisId);
  }, [usuario?.PaisId]);

  useEffect(() => {
    const cargarPaises = async () => {
      try {
        const respuesta = await new CotizadorModelo().obtenerPaises();
        setPaises(respuesta?.Data ?? []);
      } catch (error) {
        avisarErrorConsulta(error, 'los países');
      }
    };
    cargarPaises();
  }, [avisarErrorConsulta]);

  const registrarCancelacion = useCallback((key: string, canceler: () => void) => {
    canceladoresRef.current[key] = canceler;
  }, []);

  const cancelarTodasLasEdiciones = useCallback(() => {
    Object.values(canceladoresRef.current).forEach(canceler => {
      if (typeof canceler === 'function') {
        canceler();
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      cancelarTodasLasEdiciones();
    };
  }, [cancelarTodasLasEdiciones]);

  const cambiarPais = (valor: string | null) => {
    if (!valor || Number(valor) === paisId) return;
    cancelarTodasLasEdiciones();
    setPaisId(Number(valor));
  };

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sticky Header Box */}
      <Box 
        pb="md"
        pt="md"
        style={{ 
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'var(--mantine-color-body)',
          paddingLeft: '20px',
          paddingRight: '20px'
        }}
      >
        <Group justify="space-between">
          <div>
            <Group justify="space-between" align="center" mb={2} w="100%">
              <Group gap="xs" align="center">
                <Text fw={700} size="lg">
                  Configuración
                </Text>
              </Group>
            </Group>
            <Text c="dimmed" size="xs">
              Parámetros Comerciales NBS
            </Text>
          </div>
          <Select
            size="xs"
            w={180}
            label="País"
            searchable
            allowDeselect={false}
            nothingFoundMessage="Sin opciones disponibles"
            data={paises.map(p => ({ value: String(p.Id), label: p.Nombre }))}
            value={paisId ? String(paisId) : null}
            onChange={cambiarPais}
          />
        </Group>
      </Box>

      {/* Main Content with Modular Tab Controllers. Al cambiar de país se vuelven a montar las pestañas. */}
      <PaisConfiguracionContext.Provider value={paisId}>
      <div key={paisId} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0 20px 20px 20px' }}>
        <Tabs 
          value={activeTab} 
          onChange={(value) => { setActiveTab(value); cancelarTodasLasEdiciones(); }} 
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <Tabs.List mb="md">
            <Tabs.Tab value="globales" leftSection={<IconSettings size={14} />}>
              Variables Globales
            </Tabs.Tab>
            <Tabs.Tab value="logistica" leftSection={<IconTruckDelivery size={14} />}>
              Logística de Contenedores
            </Tabs.Tab>
            <Tabs.Tab value="moi" leftSection={<IconTool size={14} />}>
              M.O.D
            </Tabs.Tab>
            <Tabs.Tab value="variacion" leftSection={<IconCalendarStats size={14} />}>
              Variación Mensual Flete
            </Tabs.Tab>
            <Tabs.Tab value="comisiones" leftSection={<IconUsers size={14} />}>
              Comisiones Comerciales
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="globales" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {activeTab === 'globales' && <VariablesGlobalesCV />}
          </Tabs.Panel>

          <Tabs.Panel value="logistica" style={{ flex: 1, overflow: 'hidden' }}>
            {activeTab === 'logistica' && (
              <LogisticaCV 
                onRegistrarCancelacion={registrarCancelacion}
                onIniciarEdicionGeneral={cancelarTodasLasEdiciones}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="moi" style={{ flex: 1, overflow: 'hidden' }}>
            {activeTab === 'moi' && (
              // Las subpestañas ocupan el alto disponible; si la tabla no cabe, se hace scroll hasta la paginación.
              <Tabs defaultValue="ascensores" onChange={cancelarTodasLasEdiciones} variant="outline" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Tabs.List mb="sm">
                  <Tabs.Tab value="ascensores">Ascensores</Tabs.Tab>
                  <Tabs.Tab value="escaleras">Escaleras</Tabs.Tab>
                </Tabs.List>
                
                <Tabs.Panel value="ascensores" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <MoiAscensoresCV 
                    onRegistrarCancelacion={registrarCancelacion}
                    onIniciarEdicionGeneral={cancelarTodasLasEdiciones}
                  />
                </Tabs.Panel>
                
                <Tabs.Panel value="escaleras" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <MoiEscalerasCV 
                    onRegistrarCancelacion={registrarCancelacion}
                    onIniciarEdicionGeneral={cancelarTodasLasEdiciones}
                  />
                </Tabs.Panel>
              </Tabs>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="variacion" style={{ flex: 1, overflow: 'hidden' }}>
            {activeTab === 'variacion' && (
              <VariacionMensualCV 
                onRegistrarCancelacion={registrarCancelacion}
                onIniciarEdicionGeneral={cancelarTodasLasEdiciones}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="comisiones" style={{ flex: 1, overflow: 'hidden' }}>
            {activeTab === 'comisiones' && (
              <ComisionesCV 
                onRegistrarCancelacion={registrarCancelacion}
                onIniciarEdicionGeneral={cancelarTodasLasEdiciones}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </div>
      </PaisConfiguracionContext.Provider>
    </div>
  );
};
